// « Doigts de feu »: a situation appears, the rep presses the dialer shortcut. Key presses are read by the dialer's
// own handleDialerKey(), so the game accepts exactly the keys the dialer screen does.
import {
  handleDialerKey,
  type DialerHotkeyHandlers,
} from "../../dialer/hooks/useDialerHotkeys";
import { DIAL_OUTCOMES } from "../../domain/dialer/outcomes";
import type { DialOutcome } from "../../domain/dialer/types";

const K = "gavion.training.content.hotkeys";

/** What a key press did, in dialer terms. */
export type HotkeyAction =
  | `outcome:${DialOutcome}`
  | "skip"
  | "note"
  | "save"
  | "space"
  | "help"
  | "objections"
  | "enter"
  | "escape";

export type HotkeyPrompt = {
  id: string;
  promptKey: string;
  action: HotkeyAction;
  /** Key cap shown after a miss (« 3 », « S », « Ctrl+Entrée »…), an i18n key for named keys. */
  keyLabel: string;
};

const OUTCOME_SITUATIONS: Record<DialOutcome, string> = {
  no_answer: "rings_out",
  voicemail: "left_message",
  talked_callback: "call_me_thursday",
  demo_booked: "demo_yes",
  brochure_sent: "send_info",
  not_interested: "not_interested",
  wrong_number: "wrong_company",
  do_not_call: "never_again",
};

export const HOTKEY_PROMPTS: readonly HotkeyPrompt[] = [
  ...DIAL_OUTCOMES.map((o) => ({
    id: o.code,
    promptKey: `${K}.prompts.${OUTCOME_SITUATIONS[o.code]}`,
    action: `outcome:${o.code}` as HotkeyAction,
    keyLabel: o.hotkey,
  })),
  { id: "skip", promptKey: `${K}.prompts.skip`, action: "skip", keyLabel: "S" },
  { id: "note", promptKey: `${K}.prompts.note`, action: "note", keyLabel: "N" },
  {
    id: "objections",
    promptKey: `${K}.prompts.objections`,
    action: "objections",
    keyLabel: "O",
  },
  {
    id: "space",
    promptKey: `${K}.prompts.space`,
    action: "space",
    keyLabel: `${K}.keys.space`,
  },
  {
    id: "save",
    promptKey: `${K}.prompts.save`,
    action: "save",
    keyLabel: `${K}.keys.ctrl_enter`,
  },
  { id: "help", promptKey: `${K}.prompts.help`, action: "help", keyLabel: "?" },
];

/** Prompts per round, and the time allowed for each one. */
export const ROUND_SIZE = 12;
export const PROMPT_MS = 5_000;
/** Full speed credit under FAST_MS, none from SLOW_MS. */
export const FAST_MS = 1_000;
export const SLOW_MS = 3_000;

type KeyLike = Pick<KeyboardEvent, "key" | "ctrlKey" | "metaKey" | "altKey"> &
  Partial<Pick<KeyboardEvent, "repeat" | "isComposing">>;

/** The dialer action a key press triggers (null: the dialer ignores this key). */
export function actionOfKey(e: KeyLike): HotkeyAction | null {
  let action: HotkeyAction | null = null;
  const set = (a: HotkeyAction) => () => {
    action = a;
  };
  const handlers: DialerHotkeyHandlers = {
    onOutcome: (n) => {
      const def = DIAL_OUTCOMES.find((o) => o.hotkey === String(n));
      if (def) action = `outcome:${def.code}`;
    },
    onSkip: set("skip"),
    onNote: set("note"),
    onEnter: set("enter"),
    onSpace: set("space"),
    onEscape: set("escape"),
    onSaveShortcut: set("save"),
    onHelp: set("help"),
    onObjections: set("objections"),
  };
  // Explicit fields (a KeyboardEvent's are prototype getters, not own properties: no spread). No target: the game
  // area is neither a text field nor a button.
  handleDialerKey(
    {
      key: e.key,
      ctrlKey: e.ctrlKey,
      metaKey: e.metaKey,
      altKey: e.altKey,
      repeat: e.repeat,
      isComposing: e.isComposing,
      target: null,
    },
    handlers,
  );
  return action;
}

export type HotkeyAnswer = { correct: boolean; ms: number };

/** 0–100: 70 % accuracy, 30 % speed (only right answers earn speed). */
export function hotkeyScore(answers: readonly HotkeyAnswer[]): number {
  if (answers.length === 0) return 0;
  const accuracy = answers.filter((a) => a.correct).length / answers.length;
  const speed =
    answers.reduce(
      (s, a) =>
        s +
        (a.correct
          ? Math.min(1, Math.max(0, (SLOW_MS - a.ms) / (SLOW_MS - FAST_MS)))
          : 0),
      0,
    ) / answers.length;
  return Math.round(accuracy * 70 + speed * 30);
}

/** ROUND_SIZE prompts in random order (rng injectable for tests), each prompt at most once. */
export function drawRound(rng: () => number = Math.random): HotkeyPrompt[] {
  const pool = [...HOTKEY_PROMPTS];
  for (let i = pool.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, ROUND_SIZE);
}
